import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScheduleExport } from "../../lib/schedule-document/use-schedule-export";
import { useCustomArtStore } from "../../stores/custom-art-store";
import { exportSchedulePsd, type PsdExportOptions } from "../preview-canvas/export-psd";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";

export function PsdExportDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  const ids = { text: useId(), visibility: useId(), art: useId() };
  const hasCustomArt = useCustomArtStore((state) => state.layers.length > 0);
  const [options, setOptions] = useState<PsdExportOptions>({
    includeTextLayers: true,
    hideImageFolders: false,
    includeCustomArt: true,
  });

  const fields = [
    {
      id: ids.text,
      key: "includeTextLayers" as const,
      label: t("psd.includeTextLayers"),
      help: t("psd.includeTextLayersHelp"),
      hidden: false,
    },
    {
      id: ids.visibility,
      key: "hideImageFolders" as const,
      label: t("psd.hideImageFolders"),
      help: t("psd.hideImageFoldersHelp"),
      hidden: !options.includeTextLayers,
    },
    {
      id: ids.art,
      key: "includeCustomArt" as const,
      label: t("psd.includeCustomArt"),
      help: t("psd.includeCustomArtHelp"),
      hidden: !hasCustomArt,
    },
  ];

  const { exporting, failed, changeOpen, exportSchedule } = useScheduleExport(
    "psd",
    async (data, filename, art) => await exportSchedulePsd(data, filename, art, t("art.offline"), options),
    onOpenChange,
  );

  return (
    <Dialog open={open} onOpenChange={changeOpen}>
      <DialogContent closeDisabled={exporting}>
        <DialogHeader>
          <DialogTitle>{t("psd.title")}</DialogTitle>
          <DialogDescription>{t("psd.description")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {fields
            .filter((field) => !field.hidden)
            .map((field) => (
              <div key={field.key} className="flex items-start gap-2">
                <Checkbox
                  id={field.id}
                  className="mt-0.5"
                  checked={options[field.key]}
                  onCheckedChange={(checked) =>
                    setOptions((current) => ({ ...current, [field.key]: checked === true }))
                  }
                />
                <label htmlFor={field.id} className="space-y-0.5">
                  <span className="block font-medium">{field.label}</span>
                  <span className="block text-neutral-500">{field.help}</span>
                </label>
              </div>
            ))}
        </div>
        {failed && (
          <p role="alert" className="text-red-600">
            {t("psd.exportError")}
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" size="sm" disabled={exporting} onClick={() => changeOpen(false)}>
            {t("schedule.cancel")}
          </Button>
          <Button type="button" size="sm" disabled={exporting} aria-busy={exporting} onClick={exportSchedule}>
            {t(exporting ? "nav.saving" : "psd.export")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
